import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { getSocket } from "../../utils/socket";
import { motion } from "framer-motion";

// Hàm tiện ích để ánh xạ trạng thái với màu sắc
const getStatusStyles = (status) => {
  switch (status) {
    case "completed":
      return { backgroundColor: "bg-green-500", textColor: "text-white" }; // Nền xanh lá, chữ trắng
    case "pending":
      return { backgroundColor: "bg-yellow-500", textColor: "text-white" }; // Nền vàng, chữ trắng
    case "pending_verification":
      return { backgroundColor: "bg-orange-500", textColor: "text-white" }; // Nền cam, chữ trắng
    case "refunded":
      return { backgroundColor: "bg-purple-500", textColor: "text-white" }; // Nền tím, chữ trắng
    case "failed":
      return { backgroundColor: "bg-red-500", textColor: "text-white" }; // Nền đỏ, chữ trắng
    default:
      return { backgroundColor: "bg-gray-500", textColor: "text-white" }; // Mặc định: Nền xám, chữ trắng
  }
};

const AdminCoinTransactions = () => {
  const { fetchWithRetry, user, isLoading } = useAuth();
  const navigate = useNavigate();
  const [globalPayments, setGlobalPayments] = useState([]);
  const [localPayments, setLocalPayments] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notificationMessage, setNotificationMessage] = useState(null);

  // State cho tìm kiếm, lọc, sắp xếp, phân trang
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterMethod, setFilterMethod] = useState("");
  const [filterType, setFilterType] = useState("");
  const [sortField, setSortField] = useState("transactionDate");
  const [sortOrder, setSortOrder] = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Tải danh sách giao dịch mua coin
  const loadPayments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const globalRes = await fetchWithRetry({ method: "get", url: "/api/payments/coin-payments" });
      const localRes = await fetchWithRetry({ method: "get", url: "/api/local-payments/local-coin-payments" });

      // Log dữ liệu trả về để kiểm tra
      console.log("Global Payments Data:", globalRes.data);
      console.log("Local Payments Data:", localRes.data);

      // Lọc giao dịch mua coin (planModel là "CoinPackage")
      const globalPaymentsWithType = (globalRes.data || []).map((payment) => {
        if (payment.planModel !== "CoinPackage") {
          console.warn("Giao dịch không liên quan đến CoinPackage (global):", payment);
          return null;
        }
        return { ...payment, type: "global" };
      }).filter(payment => payment !== null);

      const localPaymentsWithType = (localRes.data || []).map((payment) => {
        if (payment.planModel !== "CoinPackage") {
          console.warn("Giao dịch không liên quan đến CoinPackage (local):", payment);
          return null;
        }
        return { ...payment, type: "local" };
      }).filter(payment => payment !== null);

      // Log dữ liệu sau khi lọc
      console.log("Filtered Global Payments:", globalPaymentsWithType);
      console.log("Filtered Local Payments:", localPaymentsWithType);

      setGlobalPayments(globalPaymentsWithType);
      setLocalPayments(localPaymentsWithType);

      const allPayments = [...globalPaymentsWithType, ...localPaymentsWithType];
      setFilteredPayments(allPayments);

      if (allPayments.length === 0) {
        setError("Không có giao dịch mua coin nào được tìm thấy.");
      }
    } catch (error) {
      console.error("Lỗi khi tải danh sách thanh toán:", error);
      if (error.message.includes("500")) {
        setError("Lỗi server khi tải danh sách thanh toán. Vui lòng thử lại sau!");
      } else if (error.message.includes("401")) {
        setError("Phiên làm việc hết hạn. Vui lòng đăng nhập lại.");
      } else {
        setError("Không thể tải danh sách thanh toán!");
      }
    } finally {
      setLoading(false);
    }
  }, [fetchWithRetry]);

  // Xử lý hoàn tiền
  const handleRefund = async (paymentId, paymentMethod, isLocalPayment) => {
    if (!window.confirm("Bạn có chắc muốn hoàn tiền cho giao dịch này?")) return;
    try {
      let url;
      if (isLocalPayment) {
        if (paymentMethod === "momo") {
          url = `/api/local-payments/refund-coin-momo-payment/${paymentId}`;
        } else if (paymentMethod === "zalopay") {
          url = `/api/local-payments/refund-coin-zalopay-payment/${paymentId}`;
        } else if (paymentMethod === "vnpay") {
          url = `/api/local-payments/refund-coin-vnpay-payment/${paymentId}`;
        } else if (paymentMethod === "vietqr") {
          url = `/api/local-payments/refund-coin-vietqr-payment/${paymentId}`;
        } else {
          throw new Error("Phương thức thanh toán nội địa không được hỗ trợ để hoàn tiền!");
        }
      } else {
        switch (paymentMethod) {
          case "binancepay":
            url = `/api/payments/refund-coin-binancepay-order/${paymentId}`;
            break;
          case "coinpayments":
            url = `/api/payments/refund-coin-coinpayments-transaction/${paymentId}`;
            break;
          case "coinbase":
            url = `/api/payments/refund-coin-coinbase-charge/${paymentId}`;
            break;
          case "nowpayments":
            url = `/api/payments/refund-coin-nowpayments-payment/${paymentId}`;
            break;
          default:
            url = `/api/payments/refund-coin-payment/${paymentId}`;
        }
      }

      const response = await fetchWithRetry({
        method: "post",
        url,
      });
      setNotificationMessage(response.data.message || "Hoàn tiền thành công!");
      loadPayments();
      setTimeout(() => setNotificationMessage(null), 3000);
    } catch (error) {
      console.error("Lỗi khi hoàn tiền:", error);
      if (error.message.includes("500")) {
        setError("Lỗi server khi hoàn tiền. Vui lòng thử lại sau!");
      } else if (error.message.includes("401")) {
        setError("Phiên làm việc hết hạn. Vui lòng đăng nhập lại.");
      } else {
        setError(error.response?.data?.message || "Không thể hoàn tiền cho giao dịch này!");
      }
    }
  };

  // Xử lý xác minh giao dịch PayPal.me
  const handleVerifyPaypalTransaction = async (paymentId, approve) => {
    if (!window.confirm(approve ? "Bạn có chắc muốn xác minh giao dịch này?" : "Bạn có chắc muốn từ chối giao dịch này?")) return;
    try {
      const response = await fetchWithRetry({
        method: "post",
        url: "/api/payments/verify-coin-paypal-transaction",
        data: { paymentId, approve },
      });
      setNotificationMessage(response.data.message || (approve ? "Xác minh giao dịch thành công!" : "Giao dịch đã bị từ chối!"));
      loadPayments();
      setTimeout(() => setNotificationMessage(null), 3000);
    } catch (error) {
      console.error("Lỗi khi xác minh giao dịch PayPal.me:", error);
      if (error.message.includes("500")) {
        setError("Lỗi server khi xác minh giao dịch. Vui lòng thử lại sau!");
      } else if (error.message.includes("401")) {
        setError("Phiên làm việc hết hạn. Vui lòng đăng nhập lại.");
      } else {
        setError(error.response?.data?.message || "Không thể xác minh giao dịch này!");
      }
    }
  };

  // Xử lý xác minh giao dịch VietQR
  const handleVerifyVietQRTransaction = async (paymentId, approve) => {
    if (!window.confirm(approve ? "Bạn có chắc muốn xác minh giao dịch này?" : "Bạn có chắc muốn từ chối giao dịch này?")) return;
    try {
      const response = await fetchWithRetry({
        method: "post",
        url: "/api/local-payments/verify-coin-vietqr-transaction",
        data: { paymentId, approve },
      });
      setNotificationMessage(response.data.message || (approve ? "Xác minh giao dịch thành công!" : "Giao dịch đã bị từ chối!"));
      loadPayments();
      setTimeout(() => setNotificationMessage(null), 3000);
    } catch (error) {
      console.error("Lỗi khi xác minh giao dịch VietQR:", error);
      if (error.message.includes("500")) {
        setError("Lỗi server khi xác minh giao dịch. Vui lòng thử lại sau!");
      } else if (error.message.includes("401")) {
        setError("Phiên làm việc hết hạn. Vui lòng đăng nhập lại.");
      } else {
        setError(error.response?.data?.message || "Không thể xác minh giao dịch này!");
      }
    }
  };

  // Xử lý xóa giao dịch
  const handleDelete = async (paymentId, isLocalPayment) => {
    if (!window.confirm("Bạn có chắc muốn xóa giao dịch này? Hành động này không thể hoàn tác!")) return;
    try {
      const cleanPaymentId = paymentId.replace(/[^a-fA-F0-9]/g, "");
      if (!/^[a-fA-F0-9]{24}$/.test(cleanPaymentId)) {
        throw new Error("paymentId không hợp lệ: " + paymentId);
      }

      const url = isLocalPayment
        ? `/api/local-payments/delete-local-coin-payment/${cleanPaymentId}`
        : `/api/payments/delete-coin-payment/${cleanPaymentId}`;
      const response = await fetchWithRetry({
        method: "delete",
        url,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });
      setNotificationMessage(response.data.message || "Xóa giao dịch thành công!");
      loadPayments();
      setTimeout(() => setNotificationMessage(null), 3000);
    } catch (error) {
      console.error("Lỗi khi xóa giao dịch:", error);
      if (error.message.includes("Unexpected token < in JSON")) {
        setError("Lỗi server: Phản hồi không đúng định dạng JSON. Vui lòng kiểm tra server!");
      } else if (error.message.includes("paymentId không hợp lệ")) {
        setError(error.message);
      } else if (error.response?.status === 404) {
        setError(error.response?.data?.message || "Giao dịch không tồn tại trong cơ sở dữ liệu!");
      } else if (error.response?.status === 500) {
        setError(error.response?.data?.message || "Lỗi server khi xóa giao dịch. Vui lòng thử lại sau!");
      } else if (error.response?.status === 401) {
        setError("Phiên làm việc hết hạn. Vui lòng đăng nhập lại.");
      } else if (error.response?.status === 403) {
        setError("Bạn không có quyền xóa giao dịch này!");
      } else {
        setError(error.response?.data?.message || "Không thể xóa giao dịch này!");
      }
    }
  };

  // Lọc, sắp xếp giao dịch
  useEffect(() => {
    let allPayments = [...globalPayments, ...localPayments];

    if (searchTerm) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      allPayments = allPayments.filter(
        (payment) =>
          payment.userId?.name?.toLowerCase().includes(lowerSearchTerm) ||
          payment.planId?.name?.toLowerCase().includes(lowerSearchTerm) ||
          payment.paymentMethod.toLowerCase().includes(lowerSearchTerm) ||
          (payment.bankTransactionId && payment.bankTransactionId.toLowerCase().includes(lowerSearchTerm)) ||
          (payment.vietqrTransactionId && payment.vietqrTransactionId.toLowerCase().includes(lowerSearchTerm))
      );
    }

    if (filterStatus) {
      allPayments = allPayments.filter((payment) => payment.status === filterStatus);
    }

    if (filterMethod) {
      allPayments = allPayments.filter((payment) => payment.paymentMethod === filterMethod);
    }

    if (filterType) {
      allPayments = allPayments.filter((payment) => payment.type === filterType);
    }

    allPayments.sort((a, b) => {
      const fieldA = a[sortField] || "";
      const fieldB = b[sortField] || "";
      if (sortField === "transactionDate") {
        return sortOrder === "asc" ? new Date(fieldA) - new Date(fieldB) : new Date(fieldB) - new Date(fieldA);
      } else if (sortField === "amount") {
        return sortOrder === "asc" ? fieldA - fieldB : fieldB - fieldA;
      } else {
        return sortOrder === "asc"
          ? fieldA.toString().localeCompare(fieldB.toString())
          : fieldB.toString().localeCompare(fieldA.toString());
      }
    });

    setFilteredPayments(allPayments);
  }, [globalPayments, localPayments, searchTerm, filterStatus, filterMethod, filterType, sortField, sortOrder]);

  // Phân trang
  const totalItems = filteredPayments.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPayments = filteredPayments.slice(startIndex, endIndex);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Kiểm tra quyền truy cập và tải dữ liệu
  useEffect(() => {
    if (isLoading) return;
    if (!user || (user.role !== "admin" && user.role !== "mod")) {
      navigate("/login");
      return;
    }
    loadPayments();

    const socket = getSocket();
    socket.on("notification", (notification) => {
      if (
        notification.type === "payment_verification_needed" ||
        notification.type === "vietqr_verification_request" ||
        notification.type === "coin_purchase" ||
        notification.type === "payment_refunded"
      ) {
        setNotificationMessage(`Giao dịch mới đã được cập nhật: ${notification.message}`);
        loadPayments();
        setTimeout(() => setNotificationMessage(null), 3000);
      }
    });

    return () => {
      socket.off("notification");
    };
  }, [user, navigate, loadPayments, isLoading]);

  if (loading) return <p className="text-center text-gray-500">Đang tải...</p>;

  return (
    <div className="p-6 min-h-screen bg-gray-50">
      <h2 className="text-3xl font-bold text-gray-800 mb-6">Quản lý giao dịch mua Coin</h2>
      {error && (
        <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-lg flex items-center justify-between">
          <span>{error}</span>
          {error.includes("Phiên làm việc hết hạn") ? (
            <button
              onClick={() => navigate("/login")}
              className="ml-4 px-4 py-1 bg-red-500 text-white rounded-lg hover:bg-red-600 transition duration-200"
            >
              Đăng nhập lại
            </button>
          ) : (
            <button
              onClick={() => setError(null)}
              className="ml-4 px-4 py-1 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition duration-200"
            >
              Đóng
            </button>
          )}
        </div>
      )}

      {notificationMessage && (
        <div className="mb-4 p-4 bg-green-100 text-green-700 rounded-lg">{notificationMessage}</div>
      )}

      {filteredPayments.length === 0 && !error && !loading && (
        <p className="text-center text-gray-500">Không có giao dịch mua coin nào được tìm thấy.</p>
      )}

      {filteredPayments.length > 0 && (
        <>
          <div className="mb-6 flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <input
                type="text"
                placeholder="Tìm kiếm theo tên, gói, phương thức, mã giao dịch..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex-1 min-w-[150px]">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Lọc theo trạng thái</option>
                <option value="completed">Hoàn tất</option>
                <option value="pending">Đang chờ</option>
                <option value="pending_verification">Đang chờ xác minh</option>
                <option value="refunded">Đã hoàn tiền</option>
                <option value="failed">Thất bại</option>
              </select>
            </div>
            <div className="flex-1 min-w-[150px]">
              <select
                value={filterMethod}
                onChange={(e) => setFilterMethod(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Lọc theo phương thức</option>
                <option value="stripe">Stripe</option>
                <option value="paypal_api">PayPal API</option>
                <option value="paypal_me">PayPal.me</option>
                <option value="coinpayments">CoinPayments</option>
                <option value="coinbase">Coinbase Commerce</option>
                <option value="nowpayments">NOWPayments</option>
                <option value="binancepay">Binance Pay</option>
                <option value="momo">MoMo</option>
                <option value="zalopay">ZaloPay</option>
                <option value="vnpay">VNPay</option>
                <option value="vietqr">VietQR</option>
              </select>
            </div>
            <div className="flex-1 min-w-[150px]">
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Lọc theo loại giao dịch</option>
                <option value="global">Quốc tế</option>
                <option value="local">Nội địa</option>
              </select>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="overflow-x-auto"
          >
            <table className="w-full bg-white rounded-lg shadow-lg">
              <thead>
                <tr className="bg-blue-500 text-white">
                  <th className="p-4 text-left cursor-pointer" onClick={() => handleSort("userId.name")}>
                    Người dùng {sortField === "userId.name" && (sortOrder === "asc" ? "↑" : "↓")}
                  </th>
                  <th className="p-4 text-left cursor-pointer" onClick={() => handleSort("userId.role")}>
                    Vai trò {sortField === "userId.role" && (sortOrder === "asc" ? "↑" : "↓")}
                  </th>
                  <th className="p-4 text-left cursor-pointer" onClick={() => handleSort("planId.name")}>
                    Gói {sortField === "planId.name" && (sortOrder === "asc" ? "↑" : "↓")}
                  </th>
                  <th className="p-4 text-left cursor-pointer" onClick={() => handleSort("amount")}>
                    Số tiền (VND) {sortField === "amount" && (sortOrder === "asc" ? "↑" : "↓")}
                  </th>
                  <th className="p-4 text-left cursor-pointer" onClick={() => handleSort("paymentMethod")}>
                    Phương thức {sortField === "paymentMethod" && (sortOrder === "asc" ? "↑" : "↓")}
                  </th>
                  <th className="p-4 text-left cursor-pointer" onClick={() => handleSort("status")}>
                    Trạng thái {sortField === "status" && (sortOrder === "asc" ? "↑" : "↓")}
                  </th>
                  <th className="p-4 text-left cursor-pointer" onClick={() => handleSort("bankTransactionId")}>
                    Mã giao dịch ngân hàng {sortField === "bankTransactionId" && (sortOrder === "asc" ? "↑" : "↓")}
                  </th>
                  <th className="p-4 text-left cursor-pointer" onClick={() => handleSort("transactionDate")}>
                    Ngày giao dịch {sortField === "transactionDate" && (sortOrder === "asc" ? "↑" : "↓")}
                  </th>
                  <th className="p-4 text-left">Hành động</th>
                </tr>
              </thead>
              <tbody>
                {currentPayments.map((payment) => {
                  // Lấy màu sắc cho trạng thái
                  const { backgroundColor, textColor } = getStatusStyles(payment.status);

                  return (
                    <tr key={payment._id} className="border-b hover:bg-gray-50 transition duration-200">
                      <td className="p-4">{payment.userId?.name || "N/A"}</td>
                      <td className="p-4">{payment.userId?.role || "N/A"}</td>
                      <td className="p-4">{payment.planId?.name || "N/A"}</td>
                      <td className="p-4">{payment.amount ? payment.amount.toLocaleString() : "N/A"}</td>
                      <td className="p-4">
                        {payment.paymentMethod === "stripe"
                          ? "Stripe"
                          : payment.paymentMethod === "paypal_api"
                          ? "PayPal API"
                          : payment.paymentMethod === "paypal_me"
                          ? "PayPal.me"
                          : payment.paymentMethod === "coinpayments"
                          ? "CoinPayments (Tiền điện tử)"
                          : payment.paymentMethod === "coinbase"
                          ? "Coinbase Commerce (Tiền điện tử)"
                          : payment.paymentMethod === "nowpayments"
                          ? "NOWPayments (Tiền điện tử)"
                          : payment.paymentMethod === "binancepay"
                          ? "Binance Pay (Tiền điện tử)"
                          : payment.paymentMethod === "momo"
                          ? "MoMo"
                          : payment.paymentMethod === "zalopay"
                          ? "ZaloPay"
                          : payment.paymentMethod === "vnpay"
                          ? "VNPay"
                          : payment.paymentMethod === "vietqr"
                          ? "VietQR"
                          : "Không xác định"}
                      </td>
                      <td className="p-4">
                        <span
                          className={`inline-block px-3 py-1 rounded-full ${backgroundColor} ${textColor}`}
                        >
                          {payment.status === "completed"
                            ? "Hoàn tất"
                            : payment.status === "pending"
                            ? "Đang chờ"
                            : payment.status === "pending_verification"
                            ? "Đang chờ xác minh"
                            : payment.status === "refunded"
                            ? "Đã hoàn tiền"
                            : "Thất bại"}
                        </span>
                      </td>
                      <td className="p-4">{payment.bankTransactionId || payment.vietqrTransactionId || "N/A"}</td>
                      <td className="p-4">{new Date(payment.transactionDate).toLocaleString("vi-VN")}</td>
                      <td className="p-4">
                        {payment.status === "completed" && (
                          <button
                            onClick={() => handleRefund(payment._id, payment.paymentMethod, payment.type === "local")}
                            className="px-3 py-1 bg-red-500 text-white rounded-lg hover:bg-red-600 mr-2"
                          >
                            Hoàn tiền
                          </button>
                        )}
                        {(payment.status === "pending" || payment.status === "pending_verification") &&
                          payment.paymentMethod === "paypal_me" &&
                          payment.type === "global" && (
                            <>
                              <button
                                onClick={() => handleVerifyPaypalTransaction(payment._id, true)}
                                className="px-3 py-1 bg-green-500 text-white rounded-lg hover:bg-green-600 mr-2"
                              >
                                Xác minh
                              </button>
                              <button
                                onClick={() => handleVerifyPaypalTransaction(payment._id, false)}
                                className="px-3 py-1 bg-red-500 text-white rounded-lg hover:bg-red-600 mr-2"
                              >
                                Từ chối
                              </button>
                            </>
                          )}
                        {(payment.status === "pending" || payment.status === "pending_verification") &&
                          payment.paymentMethod === "vietqr" &&
                          payment.type === "local" && (
                            <>
                              <button
                                onClick={() => handleVerifyVietQRTransaction(payment._id, true)}
                                className="px-3 py-1 bg-green-500 text-white rounded-lg hover:bg-green-600 mr-2"
                              >
                                Xác minh
                              </button>
                              <button
                                onClick={() => handleVerifyVietQRTransaction(payment._id, false)}
                                className="px-3 py-1 bg-red-500 text-white rounded-lg hover:bg-red-600 mr-2"
                              >
                                Từ chối
                              </button>
                            </>
                          )}
                        {payment.status === "pending" &&
                          ["coinpayments", "coinbase", "nowpayments", "binancepay"].includes(payment.paymentMethod) && (
                            <span className="text-gray-500 mr-2">
                              Đang chờ xác nhận từ{" "}
                              {payment.paymentMethod === "coinpayments"
                                ? "CoinPayments"
                                : payment.paymentMethod === "coinbase"
                                ? "Coinbase Commerce"
                                : payment.paymentMethod === "nowpayments"
                                ? "NOWPayments"
                                : "Binance Pay"}
                              ...
                            </span>
                          )}
                        <button
                          onClick={() => handleDelete(payment._id, payment.type === "local")}
                          className="px-3 py-1 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
                        >
                          Xóa
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </motion.div>

          <div className="mt-6 flex justify-between items-center">
            <div>
              <label className="mr-2">Hiển thị:</label>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="p-2 border border-gray-300 rounded-lg"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
              <span className="ml-2">mục/trang</span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-4 py-1 bg-blue-500 text-white rounded-lg disabled:bg-gray-300"
              >
                Trước
              </button>
              <span className="px-4 py-1">
                Trang {currentPage} / {totalPages}
              </span>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-4 py-1 bg-blue-500 text-white rounded-lg disabled:bg-gray-300"
              >
                Sau
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminCoinTransactions;